import React, { ComponentProps, useCallback, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { BabbelThemeProvider, themeContext } from '../BabbelThemeProvider';

import { createGlobalStyle, css } from '../custom-styled';
import { getLightTheme, getDarkTheme } from '../theme';
import { createCSSVariablesDefinition, mapToCSSVariableUsage } from './utils';

const { Provider } = themeContext;

// TODO: eventually the creation of the CSS file could be handled in the design-tokens.lib
// In this case, we wouldn't need runtime computation anymore
// We would just need to have a `theme.json` file that contains the theme mapping it to CSS variables and we would be good to go
// We could eventually even part with the theming system from styled-components if needed 🎉
const lightThemeCSSVariables = css`
  :root {
    // Using a function call here to allow lazy execution of this code, hence it will only happen when needed
    ${() => {
      return createCSSVariablesDefinition(getLightTheme());
    }}
  }
`;

const darkThemeCSSVariables = css`
  :root {
    // Using a function call here to allow lazy execution of this code, hence it will only happen when needed
    ${() => createCSSVariablesDefinition(getDarkTheme())}
  }
`;

const SystemCSSVariables = createGlobalStyle`
  ${lightThemeCSSVariables}

  @media (prefers-color-scheme: dark) {
    ${darkThemeCSSVariables}
  }
`;
const LightCSSVariables = createGlobalStyle`${lightThemeCSSVariables}`;
const DarkCSSVariables = createGlobalStyle`${darkThemeCSSVariables}`;

const ThemeCSSVariables = ({
  mode,
}: {
  mode: Required<ComponentProps<typeof BabbelThemeProvider>>['defaultMode'];
}) => {
  switch (mode) {
    case 'light':
      return <LightCSSVariables />;
    case 'dark':
      return <DarkCSSVariables />;
    case 'system':
      return <SystemCSSVariables />;
  }
};

export const ServerBabbelThemeProvider: typeof BabbelThemeProvider = ({
  children,
  defaultMode = 'light',
}) => {
  const [currentMode, setCurrentMode] = useState(defaultMode);

  const toggleMode = useCallback(() => {
    if (defaultMode === 'system') {
      console.warn(
        "BabbelThemeProvider: toggleMode is not supported while using the 'system' mode"
      );
    } else {
      setCurrentMode((currentMode) => (currentMode === 'light' ? 'dark' : 'light'));
    }
  }, [defaultMode]);

  const contextValue = useMemo(
    () => ({
      currentMode,
      toggleMode,
    }),
    [currentMode, toggleMode]
  );

  return (
    <Provider value={contextValue}>
      <ThemeCSSVariables mode={currentMode} />
      <ThemeProvider theme={mapToCSSVariableUsage(getLightTheme())}>{children}</ThemeProvider>
    </Provider>
  );
};

ServerBabbelThemeProvider.propTypes = BabbelThemeProvider.propTypes;
